import React, { useMemo, useState } from 'react';
import { Clock, DollarSign, Microscope, RefreshCw, Eye, GitCompare, Pencil } from 'lucide-react';
import { ValidationProject } from '../../types/validation';

interface Stage2ViewProps {
  projects: ValidationProject[];
  onViewProject: (projectId: string) => void;
  onViewComparison: (projectId: string) => void;
  onProjectUpdate?: () => void;
}

const Stage2View: React.FC<Stage2ViewProps> = ({ projects, onViewProject, onViewComparison }) => {
  const [flagFilter, setFlagFilter] = useState<string>('');
  const stage2Projects = useMemo(() => projects.filter(p => p.status === 'stage2'), [projects]);

  // Memoize flag type counts
  const { durationCount, costShareCount, methodCount, inconsistencyCount } = useMemo(() => ({
    durationCount: projects.filter(p =>
      p.flags.some(f => f.type === 'Benefit Duration Exceeded')
    ).length,
    costShareCount: projects.filter(p =>
      p.flags.some(f => f.type === 'Cost-Share Inconsistency')
    ).length,
    methodCount: projects.filter(p =>
      p.flags.some(f => f.type === 'Quantification Method Review')
    ).length,
    inconsistencyCount: projects.filter(p =>
      p.flags.some(f => f.type === 'System vs Document Inconsistency')
    ).length,
  }), [projects]);

  const flagTypes = [
    { key: 'Benefit Duration Exceeded', icon: <Clock className="w-6 h-6" />, title: 'Benefit Duration Exceeded', desc: 'Duration beyond crediting period', count: durationCount },
    { key: 'Cost-Share Inconsistency', icon: <DollarSign className="w-6 h-6" />, title: 'Cost-Share Inconsistency', desc: 'Funding allocation mismatch', count: costShareCount },
    { key: 'Quantification Method Review', icon: <Microscope className="w-6 h-6" />, title: 'Quantification Method Review', desc: 'Methodology requires expert validation', count: methodCount },
    { key: 'System vs Document Inconsistency', icon: <RefreshCw className="w-6 h-6" />, title: 'System vs Document Inconsistency', desc: 'Mismatch between data sources', count: inconsistencyCount },
  ];

  const filteredProjects = useMemo(() => {
    if (!flagFilter) return stage2Projects;
    return stage2Projects.filter(p => p.flags.some(f => f.type === flagFilter));
  }, [stage2Projects, flagFilter]);

  const getRiskLevel = (project: ValidationProject) => {
    return project.flags[0]?.riskLevel || 'High';
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Stage 2 - Eligibility & Risk Analysis</h2>
        <p className="text-gray-600 mt-1">Projects that passed Stage 1 but require expert review for eligibility, risk assessment, or methodology validation.</p>
      </div>

      {/* Risk Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {flagTypes.map((flag) => (
          <button
            key={flag.key}
            onClick={() => setFlagFilter(flagFilter === flag.key ? '' : flag.key)}
            className={`bg-white rounded-lg border p-4 hover:shadow-md transition-all text-left ${
              flagFilter === flag.key ? 'border-red-500 ring-2 ring-red-200' : 'border-red-200'
            }`}
          >
            <div className="text-red-500 mb-2">{flag.icon}</div>
            <h4 className="font-semibold text-gray-900 text-sm">{flag.title}</h4>
            <p className="text-xs text-gray-500 mt-1">{flag.desc}</p>
            <span className="inline-block mt-2 px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
              {flag.count} projects
            </span>
          </button>
        ))}
      </div>

      {/* Flagged Projects Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Flagged Projects - Stage 2 ({filteredProjects.length})
            {flagFilter && (
              <button
                onClick={() => setFlagFilter('')}
                className="ml-3 text-sm font-normal text-red-600 hover:text-red-800"
              >
                Clear filter
              </button>
            )}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" aria-label="Stage 2 flagged projects">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project ID</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk Level</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flag Reason</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location/Basin</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredProjects.map(project => (
                <tr key={project.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{project.externalId || project.id}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{project.name}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      project.type === 'new' ? 'bg-cyan-100 text-cyan-800' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {project.type === 'new' ? 'New' : 'Historic'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                      {getRiskLevel(project)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {project.flags.map(f => f.type).join(', ')}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{project.details.basin}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => onViewProject(project.id)}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-1"
                        aria-label={`View ${project.name}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                        View
                      </button>
                      {project.flags.some(f => f.type === 'System vs Document Inconsistency') ? (
                        <button
                          onClick={() => onViewComparison(project.id)}
                          className="px-3 py-1.5 text-sm font-medium text-cyan-700 bg-cyan-50 hover:bg-cyan-100 rounded-lg transition-colors flex items-center gap-1"
                          aria-label={`Compare ${project.name}`}
                        >
                          <GitCompare className="w-3.5 h-3.5" />
                          Compare
                        </button>
                      ) : (
                        <button
                          onClick={() => onViewComparison(project.id)}
                          className="px-3 py-1.5 text-sm font-medium text-cyan-700 bg-cyan-50 hover:bg-cyan-100 rounded-lg transition-colors flex items-center gap-1"
                          aria-label={`Edit ${project.name}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          Edit
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredProjects.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    No projects flagged in Stage 2
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Stage2View;
